import React from "react";
import { Text } from "@visx/text";

/*
 * Types
 */

export interface BarData {
    label: string;
    value: number;
}

export type LabelPosition = "top" | "on";

export interface BarProps extends BarData {
    color: string;
    height: number;
    width: number;
    x: number;
    y: number;
    labelPosition?: LabelPosition;
}

/*
 * Main
 */

const rx = 5;
const spacing = 9;
const labelOffset = 20; // Offset for when the label is above the bar

export const Bar = (props: BarProps): React.ReactElement => {
    const { color, height, label, value, width, x, y, labelPosition = "on", ...rest } = props;
    const barHeight = height - spacing;

    const svgFontStyles = {
        fill: getContrastingBWColor(color),
        fontFamily: "Helvetica",
        fontSize: "12px",
        fontWeight: 400,
    };

    function getContrastingBWColor(hexColor: string) {
        // Parse the hex color
        const hex = hexColor.replace("#", "");
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        // Calculate the brightness using the formula for luminance
        // This takes into account the different perception of each color by the human eye
        const brightness = (r * 0.299 + g * 0.587 + b * 0.114) / 255;

        // If the brightness is greater than the threshold (0.5 is common), return black, otherwise white
        return brightness > 0.5 ? "#000000" : "#FFFFFF";
    }

    const formatValue = (value: number): string => {
        return Number(value.toFixed(2)).toString();
    };

    const isWholeNumber = (value: number): boolean => {
        return Number.isInteger(value);
    };

    const dxValue = isWholeNumber(value) ? -20 : -35;

    const isLabelAbove = labelPosition === "top";
    const labelY = isLabelAbove ? 10 : 18;
    const dataY = isLabelAbove ? labelY + 28 : 18;
    const barY = isLabelAbove ? y + labelOffset : y; // Push the bar down if the label is above

    return (
        <g>
            <svg fill={color} {...rest}>
                <path
                    d={`M${x},${barY} h${width} q${rx},0 ${rx},${rx} v${barHeight} q0,${rx} -${rx},${rx} h-${width} z`}
                />
            </svg>
            <Text
                data-test-id={`bar-label-text-${label}`}
                dx={10}
                dy={labelY}
                style={svgFontStyles}
                x={x}
                y={isLabelAbove ? y : barY}
            >
                {label}
            </Text>
            <Text
                data-test-id={`bar-label-text-${value}`}
                dx={dxValue}
                dy={dataY}
                style={svgFontStyles}
                x={width}
                y={isLabelAbove ? y : barY}
            >
                {formatValue(value)}
            </Text>
        </g>
    );
};
